import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import useMedian from '../../../hooks/useMedian';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    reviewsByListingId,
    distancesByListingId,
    reviews,
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    listingsAreLoaded,
    totalItems,
  } = props;
  const { isMedian } = useMedian();
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';
  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({
          id: 'SearchResultsPanel.screenreader.pagination',
        })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  const hasResults = !(listingsAreLoaded && totalItems === 0);

  return (
    <div>
      <div className={classes}>
        <div
          className={classNames(isMapVariant ? css.listingCardsMapVariant : css.listingCards, {
            [css.listingCardsMapVariantMedian]: isMapVariant && isMedian,
          })}
        >
          {listings.map(l => {
            // ⬇️ This is the key change ⬇️
            // Each listing object now contains its reviews.
            // We extract them here and pass them to the ListingCard.
            const reviews = reviewsByListingId ? (reviewsByListingId[l.id.uuid] || []) : [];
            const distance = distancesByListingId ? distancesByListingId[l.id.uuid] : null;
            return (
              <ListingCard
                reviews={reviews}
                distance={distance}
                className={css.listingCard}
                key={l.id.uuid}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
                dates={search.dates}
                start={search.start}
                end={search.end}
                petType={search.pub_pet_type}
                dogSize={search.pub_dog_size || search.pub_dog_size}
              />
            );
          })}
          {props.children}
        </div>
        {paginationLinks}
      </div>
      {hasResults && !isMedian ? (
        <div style={{ fontStyle: 'italic', textAlign: 'center' }} className={css.linkContainer}>
          To view more providers in your town, please zoom out on the map.
        </div>
      ) : null}
      {!isMedian && (
        <div style={{ fontStyle: 'italic', textAlign: 'center' }} className={css.linkContainer}>
          Can’t find what you’re looking for? Contact{' '}
          <a
            className={css.link}
            href="mailto:support@purfurry.com?subject=Help%20Finding%20a%20Service&body=Hi%20Purfurry%20Team,%0D%0A%0D%0AI%20was%20searching%20for%20a%20service%20but%20couldn%E2%80%99t%20find%20an%20exact%20match.%0D%0A%0D%0APlease%20help%20me%20find%20a%20suitable%20listing.%0D%0A%0D%0AMy%20requirements%20are:%0D%0A[Add%20details%20here]%0D%0AAddress:%20[Your%20Address]%0D%0AProvider%20Name%20(if%20any):%20[Provider%20Name]%0D%0ADrop-off%20Date:%20[Drop-off%20Date]%0D%0APick-up%20Date:%20[Pick-up%20Date]%0D%0A%0D%0AThank%20you!"
          >
            support@purfurry.com
          </a>
        </div>
      )}
    </div>
  );
};

export default SearchResultsPanel;
